import React, { FC, ReactNode, useContext, useState } from 'react';
import { NavLink as RouterLink, NavLinkProps as RouterLinkProps } from 'react-router-dom';
import PropTypes from 'prop-types';
import clsx from 'clsx';
// material ui components
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { Badge, Box, Button, Card, Collapse, IconButton, ListItem, SvgIcon, useMediaQuery } from '@mui/material';
// sidebar components
import { SidebarContext } from '../../../contexts/SidebarContext';
// icons
import ExpandLessTwoToneIcon from '@mui/icons-material/ExpandLessTwoTone';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';

interface SidebarMenuItemProps {
    children?: ReactNode;
    link?: string;
    icon?: any;
    activeIcon?: any;
    badge?: string;
    open?: boolean;
    active?: boolean;
    isMain?: boolean;
    name: string;
}

const MenuIcon = styled(Card)(
    ({ theme }) => `
  &.MuiPaper-root.MuiCard-root{
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50px;
    width: 50px;
    border-radius: 10px;
    box-shadow: none;
    background-color: ${alpha(theme.palette.primary.main, 0.04)};
  }
  &.MuiPaper-root.MuiCard-root.active{
    background-color: ${theme.palette.purple.light};
  }
  `
);

function CutomNavlink(props) {
    const matches = useMediaQuery('(min-width:1280px)');
    const { to, name, badge, Icon, activeClass, onClick } = props;
    const CustomLink = React.useMemo(
        () =>
            React.forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'>>(function NavLink(linkProps, ref) {
                return (
                    <RouterLink
                        ref={ref}
                        to={to}
                        className={({ isActive }) => (isActive ? activeClass : undefined)}
                        {...linkProps}
                    />
                );
            }),
        [to, activeClass]
    );

    return (
        <Box
            sx={{
                p: 0,
                display: 'flex',
                mt: { lg: '0', xs: '25px' },
                textAlign: { lg: 'left', xs: 'center' },
                flexDirection: { lg: 'row', xs: 'column' },
                alignItems: { lg: 'center', xs: 'center' },
                justifyContent: { lg: 'start', xs: 'center' },
            }}
        >
            {Icon && (
                <IconButton
                    sx={{ p: 0, display: { xs: 'block', lg: 'none' } }}
                    onClick={onClick}
                    component={CustomLink}
                    disableRipple
                >
                    <SvgIcon sx={{ display: 'block', fontSize: { lg: '22px', xs: '50px' } }} viewBox='0 0 22 22'>
                        <Icon />
                    </SvgIcon>
                </IconButton>
            )}
            <Button
                {...props.btnProps}
                component={CustomLink}
                onClick={onClick}
                startIcon={matches ? <Icon /> : null}
                sx={{ lineHeight: '1.2' }}
            >
                {name}
                {badge && <Badge badgeContent={badge} />}
            </Button>
        </Box>
    );
}

const SidebarMenuItem: FC<SidebarMenuItemProps> = ({
    children,
    link,
    icon: Icon,
    activeIcon: ActiveIcon,
    badge,
    open: openParent,
    active,
    isMain,
    name,
    ...rest
}) => {
    const [menuToggle, setMenuToggle] = useState(openParent);
    const { toggleSidebar } = useContext(SidebarContext);

    const toggleMenu = (): void => {
        setMenuToggle((Open) => !Open);
    };

    if (children) {
        return (
            <ListItem component='div' className={clsx({ active: menuToggle }, 'Mui-children')} key={name} {...rest}>
                {Icon && isMain && (
                    <MenuIcon className={clsx({ active: menuToggle })}>
                        <Icon />
                    </MenuIcon>
                )}
                <Box sx={{ display: 'flex' }}>
                    <Button
                        className={clsx({ 'Mui-active': menuToggle })}
                        startIcon={Icon && !isMain && <Icon />}
                        onClick={toggleMenu}
                        sx={{
                            px: 0,
                            lineHeight: '1.2',
                            minWidth: 'auto',
                            '&:hover': {
                                backgroundColor: 'transparent',
                            },
                        }}
                        disableRipple
                        endIcon={menuToggle ? <ExpandLessTwoToneIcon /> : <ExpandMoreTwoToneIcon />}
                    >
                        {name}
                    </Button>
                </Box>
                <Collapse in={menuToggle}>{children}</Collapse>
            </ListItem>
        );
    }

    return (
        <ListItem component='div' key={name} {...rest} sx={{ justifyContent: { xs: 'center', lg: 'start' } }}>
            <CutomNavlink
                to={link}
                onClick={toggleSidebar}
                Icon={active ? ActiveIcon : Icon}
                activeClass='Mui-active'
                name={name}
                badge={badge}
                btnProps={{ disableRipple: true, color: 'primary' }}
            />
        </ListItem>
    );
};

SidebarMenuItem.propTypes = {
    children: PropTypes.node,
    active: PropTypes.bool,
    link: PropTypes.string,
    icon: PropTypes.elementType,
    badge: PropTypes.string,
    open: PropTypes.bool,
    isMain: PropTypes.bool,
    name: PropTypes.string.isRequired,
};

SidebarMenuItem.defaultProps = {
    open: false,
    active: false,
};

export default SidebarMenuItem;
